import { Card, Link, Stepper, Typography, useToast } from "@hellocarbo/lasagne";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  useFetcher,
  useLoaderData,
} from "react-router";

import background from "@/assets/fr/sign-up/background.png";
import LanguageSelector from "@/components/LanguageSelector";
import Carousel from "@/components/SignUp/Carousel";
import CompanyStep from "@/components/SignUp/Form/CompanyStep";
import EmailStep from "@/components/SignUp/Form/EmailStep";
import PasswordStep from "@/components/SignUp/Form/PasswordStep";
import PhoneNumberStep from "@/components/SignUp/Form/PhoneNumberStep";
import Loader from "@/components/ui/Loader";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import useSignupSteps from "@/hooks/useSignupSteps";
import { cn } from "@/utils/classes";

import PasswordConstraints from "./PasswordConstraints";

export async function loader({ params }: LoaderFunctionArgs) {
  return fetch(`/api/client/industry?locale=${params.lang ?? "fr"}`);
}

export async function action({ request }: ActionFunctionArgs) {
  const csrfToken = document?.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]'
  )?.content;

  const response = await fetch("/users", {
    method: request.method,
    headers: {
      "X-CSRF-Token": `${csrfToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(await request.json()),
  });

  if (response.status === 201) {
    window.location.href = "/";
    return { ok: true };
  }

  if (response.status === 422) {
    return { ok: false, error: await response.json() };
  }

  if (response.status === 400) {
    return {
      ok: false,
      error: { errors: { account: "account already exists" } },
    };
  }

  return { ok: false };
}

let hasSegmentEventBeenSent = false;

const SignUp = () => {
  const industryResponse = useLoaderData<typeof loader>();

  const [fetcherKey, setFetcherKey] = useState(`${Date()}`);

  const fetcher = useFetcher({ key: fetcherKey });
  const { toast } = useToast();

  const { t } = useTranslation(["common", "signUp", "passwordConstraints"]);

  const { currentStep, goToStep } = useSignupSteps();

  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");

  const [phoneNumber, setPhoneNumber] = useState("");

  const { sendSegmentEvent } = useSegmentEvent();

  const enum Steps {
    Email = 1,
    Password = 2,
    Company = 3,
    PhoneNumber = 4,
    Submitting = 5,
  }

  // If we're past the first step and have no email, go to the first step
  useEffect(() => {
    if (currentStep > 1 && !email) {
      goToStep(Steps.Email);
    }
  }, [Steps.Email, currentStep, email, goToStep]);

  // Show an error toast if the form submission fails
  useEffect(() => {
    if (fetcher.data && !fetcher.data.ok) {
      if (fetcher.data.error?.errors?.email) {
        toast({
          title: t("signUp:email.error"),
          intent: "negative",
        });
      }
      if (fetcher.data.error?.errors?.password) {
        toast({
          title: t("signUp:password.error"),
          intent: "negative",
        });
      }
      if (fetcher.data.error?.errors?.company) {
        toast({
          title: t("signUp:company.error"),
          intent: "negative",
        });
      }
      if (fetcher.data.error?.errors?.phone_number) {
        toast({
          title: t("signUp:phoneNumber.error"),
          intent: "negative",
        });
      }
      if (fetcher.data.error?.errors?.account) {
        toast({
          title: t("signUp:accountAlreadyExists"),
          intent: "negative",
        });
      }

      if (!fetcher.data.error) {
        toast({
          title: t("somethingWentWrong"),
          intent: "negative",
        });
      }

      goToStep(Steps.Email);
      setFetcherKey(`${Date()}`);
    }
  }, [fetcher.data, goToStep, toast, t, Steps.Email]);

  // Submit if we're on the last step and have all the required data
  useEffect(() => {
    if (
      !fetcher.data &&
      fetcher.state !== "submitting" &&
      currentStep === Steps.Submitting
    ) {
      if (email && password && companyName && industry) {
        fetcher.submit(
          {
            user: {
              email: email,
              password: password,
              ...(phoneNumber && { phone_number: phoneNumber }),
              company: {
                name: companyName,
                industry_id: industry,
              },
            },
          },
          {
            method: "post",
            encType: "application/json",
          }
        );
      }
    }
  }, [
    email,
    password,
    companyName,
    industry,
    phoneNumber,
    fetcher,
    currentStep,
    Steps.Submitting,
  ]);

  const isNewSignup =
    // Can't rely on the data from the feature context since it's not reliable for AB tests
    document.getElementById("new_signup_page")?.dataset.enabled === "true";

  useEffect(() => {
    if (!hasSegmentEventBeenSent && isNewSignup) {
      sendSegmentEvent("visite_page_inscription_B");
      hasSegmentEventBeenSent = true;
    }
  }, [sendSegmentEvent, isNewSignup]);

  if (!isNewSignup) {
    return <PasswordConstraints />;
  }

  // Submitting
  if (currentStep === Steps.Submitting) {
    return <Loader />;
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case Steps.Password:
        return <PasswordStep password={password} setPassword={setPassword} />;
      case Steps.Company:
        return (
          <CompanyStep
            companyName={companyName}
            setCompanyName={setCompanyName}
            industry={industry}
            setIndustry={setIndustry}
            industryResponse={industryResponse}
          />
        );
      case Steps.PhoneNumber:
        return (
          <PhoneNumberStep
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        "gap-lg p-xl relative grid min-h-screen w-screen grid-cols-1 items-center justify-center overflow-hidden sm:grid-cols-2",
        currentStep > 1 ? "pt-lg" : ""
      )}
    >
      <div className="top-md right-md absolute z-20">
        <LanguageSelector />
      </div>
      {currentStep === Steps.Email ? (
        <>
          <div className="mx-auto flex h-full max-w-[440px] flex-col justify-between">
            <EmailStep email={email} setEmail={setEmail} />
            <Typography step="-1" as="p" className="text-grayscale text-center">
              <Trans
                t={t}
                i18nKey="signUp:CGU"
                components={{
                  1: (
                    <Link
                      href="https://docs.google.com/document/d/1lNgCsCNnni156XmllauaSwL7KIsEOZT9"
                      size="medium"
                      className="text-sm"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </Typography>
          </div>
          <Carousel />
        </>
      ) : (
        <div className="gap-md col-span-2 flex h-full w-full flex-col items-center justify-between justify-self-center">
          <Card isFlat className="w-full max-w-[560px]">
            {getStepContent(currentStep)}
          </Card>
          <Stepper steps={3} currentStep={currentStep - 1} />
        </div>
      )}
      <img
        src={background}
        className="absolute top-0 left-0 z-[-1] h-full w-full scale-[1.02] object-cover blur-[5px]"
      />
    </div>
  );
};

export default SignUp;
